import React from "react";

/* ============================================================
   useBodyLock — ref-counted page scroll lock.

   Problem we're solving: multiple stacking modals (product modal
   + drop drawer + image lightbox) each independently set
   `body.style.overflow`, `position:fixed`, etc. on mount. When
   the inner modal unmounts, it restores the *snapshot it took*,
   which is the OUTER modal's locked values — leaving the page
   stuck or, worse, restoring lock state that the outer modal
   already cleaned up.

   This hook keeps a global counter. The FIRST caller locks the
   page; every subsequent caller just increments. The LAST caller
   to unmount releases the lock and restores the user's original
   scroll position. Safe for any nesting depth.
   ============================================================ */

let __lockCount = 0;
let __saved = null;
let __scrollY = 0;

// iOS Safari is the only browser that REQUIRES the `position: fixed` body
// dance to prevent scroll bleed-through under a modal. Every other browser
// honours `overflow: hidden` alone — and `overflow: hidden` preserves the
// scroll position natively, so no `scrollTo` restore is needed (and no
// "jump to top → glide back" flash).
const IS_IOS = typeof navigator !== "undefined"
  && /iPad|iPhone|iPod/.test(navigator.userAgent || "")
  && !(typeof window !== "undefined" && window.MSStream);

function acquire() {
  __lockCount += 1;
  if (__lockCount > 1) return;

  __scrollY = window.scrollY || window.pageYOffset || 0;
  __saved = {
    bodyOverflow:    document.body.style.overflow,
    htmlOverflow:    document.documentElement.style.overflow,
    bodyPosition:    document.body.style.position,
    bodyTop:         document.body.style.top,
    bodyWidth:       document.body.style.width,
    bodyPaddingRight:document.body.style.paddingRight,
  };
  // Compensate for the scrollbar width so the layout doesn't shift
  // sideways when overflow:hidden is applied.
  const sbw = window.innerWidth - document.documentElement.clientWidth;
  if (sbw > 0) document.body.style.paddingRight = `${sbw}px`;

  document.body.style.overflow = "hidden";
  document.documentElement.style.overflow = "hidden";

  // Only do the position:fixed dance on iOS (where overflow:hidden alone
  // isn't enough to stop the page from scrolling under the modal).
  if (IS_IOS) {
    document.body.style.position = "fixed";
    document.body.style.top = `-${__scrollY}px`;
    document.body.style.width = "100%";
  }

  document.body.classList.add("pdm-open");
}

function release() {
  __lockCount = Math.max(0, __lockCount - 1);
  if (__lockCount > 0) return;
  if (!__saved) return;

  // CSS sets `scroll-behavior: smooth` globally. We must temporarily
  // disable it before any scroll-restore so the browser doesn't *animate*
  // the page back to its original position (which the user perceives as
  // "page jumps to top then glides back down").
  const html = document.documentElement;
  const prevSB = html.style.scrollBehavior;
  html.style.scrollBehavior = "auto";

  document.body.style.overflow      = __saved.bodyOverflow;
  document.documentElement.style.overflow = __saved.htmlOverflow;
  document.body.style.position      = __saved.bodyPosition;
  document.body.style.top           = __saved.bodyTop;
  document.body.style.width         = __saved.bodyWidth;
  document.body.style.paddingRight  = __saved.bodyPaddingRight;
  document.body.classList.remove("pdm-open");

  // Only iOS needs an explicit scroll restore (since we used position:fixed
  // there which loses the scroll position). On every other browser the
  // page is already at __scrollY because we never reset it.
  if (IS_IOS) {
    window.scrollTo(0, __scrollY);
  }

  requestAnimationFrame(() => {
    html.style.scrollBehavior = prevSB;
  });

  __saved = null;
}

export function useBodyLock(active = true) {
  React.useEffect(() => {
    if (!active) return;
    acquire();
    return () => release();
  }, [active]);
}
